import React, { memo, useCallback, useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import Portal from '@atlaskit/portal';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { layers } from '@atlaskit/theme/constants';
import Tooltip from '@atlaskit/tooltip';
import { ResourcedEmoji } from '@atlaskit/emoji';
import type { EmojiId } from '@atlaskit/emoji/types';
import type { EmojiProvider } from '@atlaskit/emoji';

import { getOptimisticImageURL } from '@confluence/fabric-emoji-support';

import { EMOJI_SIZE } from './emojiConfig.ts';
import { useBloomAnimation } from './useBloomAnimation.ts';
import { BloomAnimationEmoji } from './BloomAnimationEmoji.tsx';

const OMNIBAR_ANALYTICS_SOURCE = 'confluenceOmniBar';

const HOVER_TILT_MIN_DEG = -15;
const HOVER_TILT_MAX_DEG = 15;
const HOVER_LIFT_PX = 15;
const HOVER_SCALE = 1.8;

function getRandomHoverTilt(): number {
	return (
		Math.floor(Math.random() * (HOVER_TILT_MAX_DEG - HOVER_TILT_MIN_DEG + 1)) + HOVER_TILT_MIN_DEG
	);
}

const styles = cssMap({
	emojiButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.075'),
		paddingInline: token('space.075'),
		margin: '0',
		marginRight: token('space.100'),
		border: 'none',
		width: '32px',
		minWidth: '32px',
		height: '32px',
		borderRadius: token('radius.small'),
		flexShrink: 0,
		pointerEvents: 'auto',
		backgroundColor: 'transparent',
		overflow: 'visible',
	},
	emojiInnerContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: 'transparent',
		width: '100%',
		height: '100%',
		overflow: 'visible',
		transition: 'transform 0.2s ease-out',
	},
	tooltipTriggerWrapper: {
		paddingBlockStart: token('space.200'),
		marginBlockStart: token('space.negative.200'),
		display: 'inline-block',
		overflow: 'visible',
	},
});

interface DefaultReactionButtonProps {
	emojiId: EmojiId;
	emojiProvider: Promise<EmojiProvider>;
	reacted: boolean;
	onClick: () => void;
	tooltipDescriptor?: MessageDescriptor;
	cloudId?: string;
}

const DefaultReactionButtonInner: React.FC<DefaultReactionButtonProps> = ({
	emojiId,
	emojiProvider,
	cloudId,
	reacted,
	onClick,
	tooltipDescriptor,
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { animatingEmojis, triggerBloomAnimation } = useBloomAnimation();
	const [isTooltipVisible, setIsTooltipVisible] = useState(true);
	const [isHovered, setIsHovered] = useState(false);
	const [hoverTilt, setHoverTilt] = useState(0);

	const emojiHoverTransform = isHovered
		? `translateY(-${HOVER_LIFT_PX}px) scale(${HOVER_SCALE}) rotate(${hoverTilt}deg)`
		: 'translateY(0) scale(1) rotate(0deg)';

	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'omniBarEmojiSlot',
					source: OMNIBAR_ANALYTICS_SOURCE,
					attributes: { emojiId: emojiId.id || '' },
				},
			}).fire();

			setIsTooltipVisible(false);
			setIsHovered(false);

			const rect = event.currentTarget.getBoundingClientRect();
			const x = rect.left + rect.width / 2 - 10;
			const y = rect.top + rect.height / 2 - 20;

			triggerBloomAnimation(x, y, emojiId.id || '');

			if (!reacted && onClick) {
				onClick();
			}
		},
		[onClick, emojiId.id, reacted, triggerBloomAnimation, createAnalyticsEvent],
	);

	const handleMouseEnter = useCallback(() => {
		setIsTooltipVisible(true);
		setHoverTilt(getRandomHoverTilt());
		setIsHovered(true);
	}, []);

	const handleMouseLeave = useCallback(() => {
		setIsHovered(false);
	}, []);

	const handleFocus = useCallback(() => {
		setIsTooltipVisible(true);
	}, []);

	const ariaLabel = tooltipDescriptor ? formatMessage(tooltipDescriptor) : undefined;

	const button = (
		<Pressable
			onClick={handleClick}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onFocus={handleFocus}
			xcss={styles.emojiButton}
			aria-label={ariaLabel}
		>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Transform is dynamic (isHovered, hoverTilt); transition is in xcss */}
			<Box xcss={styles.emojiInnerContainer} style={{ transform: emojiHoverTransform }}>
				<ResourcedEmoji
					emojiProvider={emojiProvider}
					emojiId={emojiId}
					showTooltip={false}
					fitToHeight={EMOJI_SIZE}
					optimisticImageURL={
						cloudId && emojiId.id ? getOptimisticImageURL(cloudId, emojiId.id, 'MDPI') : undefined
					}
				/>
			</Box>
		</Pressable>
	);

	return (
		<>
			{ariaLabel && isTooltipVisible ? (
				<Tooltip content={ariaLabel} position="top">
					<Box xcss={styles.tooltipTriggerWrapper}>{button}</Box>
				</Tooltip>
			) : (
				button
			)}
			{typeof document !== 'undefined' && animatingEmojis.length > 0 && (
				<Portal zIndex={layers.spotlight()}>
					{animatingEmojis.map((animatingEmoji) => (
						<BloomAnimationEmoji
							key={animatingEmoji.id}
							animatingEmoji={animatingEmoji}
							emojiProvider={emojiProvider}
							emojiSize={EMOJI_SIZE}
						/>
					))}
				</Portal>
			)}
		</>
	);
};

export const DefaultReactionButton = memo(DefaultReactionButtonInner);
DefaultReactionButton.displayName = 'DefaultReactionButton';
